import React, { forwardRef, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { Box, Grid, Tooltip, Typography } from "@mui/material";
import TextFieldInput from "../../../components/TextFieldInput";
import BoxCheckInput from "../../../components/BoxCheckInput";
import CustomDatePicker from "../../../components/CustomDatePicker";
import {
  COMPARE_VALUE,
  DATE_START_YEAR,
  IDENTITY,
  MARITAL_STATUS,
  MOTHER_TONGUE_OPTIONS,
  OTHERS,
  STATUS,
} from "../../../config/constant";
import { useDispatch, useSelector } from "react-redux";
import SnackbarAlert from "../../../shared-components/snackbar-alert/SnackbarAlert";
import CommonButton from "../../../shared-components/common-button/CommonButton";
import { useTranslation } from "react-i18next";
import {
  getApplicantDetails,
  setActiveStep,
  setApplicantActiveStep,
  setApplicantTotal,
  setEditPreviewApplicant,
  setHasContinuedAsMinorModal,
  setKycVerificationActiveStep,
  setStepsCompleted,
  updateApplicantDetailsForm,
} from "../new-booking-form.slice";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { RHFPhoneInput } from "../../../shared-components/phone-input/RHFPhoneInput";
import {
  getErrorMessage,
  getLastStep,
  isValidPhoneNumberWithRules,
} from "../../../utilis/common-funtions";
import SelectInput from "../../../components/SelectInput";
import AddressFields from "./AddressFields";
import { route } from "../../../services/apiRoutes";
import { useHistory, useParams } from "react-router-dom";
import AddAuthorizedSignatoryButton from "../components/AddAuthorizedSignatoryButton";
import SearchableAutoComplete from "../../../components/SearchableAutoComplete";

const AdditionalInfo = forwardRef(({ applicantNo }, ref) => {
  const { t } = useTranslation();
  const { oppId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const [showAlternateNumberField, setShowAlternateNumberField] =
    useState(false);
  const [snackbarConfig, setSnackBarconfig] = useState({
    open: false,
    message: "",
    severity: "",
  });
  // eslint-disable-next-line no-unused-vars
  const [applicantObject, setApplicantObject] = useState("");
  
  const {
    masterData,
    applicantData,
    activeStep,
    stepsCompleted,
    applicantActiveStep,
    hasContinuedAsMinor,
    applicantTotal,
  } = useSelector((state) => state?.newBookingForm);
  const IsMarried =
    applicantData[`applicant${applicantNo}`]?.personalDetails?.relation ===
      IDENTITY.WIFE_OF ||
    applicantData[`applicant${applicantNo}`]?.personalDetails?.relation ===
      IDENTITY.HUSBAND_OF;

  const additionalInfoFormik = useFormik({
    initialValues: {
      permanentAddress: {
        areaName: "",
        houseNumber: "",
        city: "",
        state: "",
        country: "",
        pinCode: null,
      },
      communicationAddress: {
        areaName: "",
        houseNumber: "",
        city: "",
        state: "",
        country: "",
        pinCode: null,
      },
      isSameAddress: 1,
      countryCode: "IN",
      alternateCountryCode: "IN",
      contactNumber: "",
      alternateContactNumber: "",
      emailAddress: "",
      motherTongue: "",
      motherTongueIfOthers:"",
      maritalStatus: "",
      anniversaryDate: "",
      dob: "",
    },
    validationSchema: yup.object().shape({
      permanentAddress: yup.object().shape({
        areaName: yup
          .string()
          .required(t("addressFields.validations.areaName")),
        houseNumber: yup
          .string()
          .required(t("addressFields.validations.houseNumber")),
        city: yup.string().required(t("addressFields.validations.city")),
        state: yup.string().when("country", {
          is: (country) => !country || country === "India",
          then: () =>
            yup.string().required(t("addressFields.validations.state")),
          otherwise: () => yup.string().notRequired(),
        }),
        country: yup.string().required(t("addressFields.validations.country")),
        pinCode: yup.string().when("country", {
          is: (country) => !country || country === "India",
          then: () =>
            yup.string().required(t("addressFields.validations.pincode")),
          otherwise: () => yup.string().notRequired(),
        }),
      }),
      communicationAddress: yup.object().shape({
        areaName: yup.string().when("isSameAddress", {
          is: (isSameAddress) =>
            isSameAddress === 0 ||
            additionalInfoFormik?.values?.isSameAddress === 0,
          then: () =>
            yup.string().required(t("addressFields.validations.areaName")),
          otherwise: () => yup.string().notRequired(),
        }),
        houseNumber: yup.string().when("isSameAddress", {
          is: (isSameAddress) =>
            isSameAddress === 0 ||
            additionalInfoFormik?.values?.isSameAddress === 0,
          then: () =>
            yup.string().required(t("addressFields.validations.houseNumber")),
          otherwise: () => yup.string().notRequired(),
        }),
        city: yup.string().when("isSameAddress", {
          is: (isSameAddress) =>
            isSameAddress === 0 ||
            additionalInfoFormik?.values?.isSameAddress === 0,
          then: () =>
            yup.string().required(t("addressFields.validations.city")),
          otherwise: () => yup.string().notRequired(),
        }),
        state: yup.string().when("isSameAddress", {
          is: (isSameAddress) =>
            isSameAddress === 0 ||
            additionalInfoFormik?.values?.isSameAddress === 0,
          then: () =>
            additionalInfoFormik?.values?.permanentAddress?.country === "" ||
            additionalInfoFormik?.values?.permanentAddress?.country === "India"
              ? yup.string().required(t("addressFields.validations.state"))
              : yup.string().notRequired(),
          otherwise: () => yup.string().notRequired(),
        }),
        country: yup.string().when("isSameAddress", {
          is: (isSameAddress) =>
            isSameAddress === 0 ||
            additionalInfoFormik?.values?.isSameAddress === 0,
          then: () =>
            yup.string().required(t("addressFields.validations.country")),
          otherwise: () => yup.string().notRequired(),
        }),
        pinCode: yup.string().when("isSameAddress", {
          is: (isSameAddress) =>
            isSameAddress === 0 ||
            additionalInfoFormik?.values?.isSameAddress === 0,
          then: () =>
            additionalInfoFormik?.values?.permanentAddress?.country === "" ||
            additionalInfoFormik?.values?.permanentAddress?.country === "India"
              ? yup
                  .number()
                  .nullable()
                  .required(t("addressFields.validations.pincode"))
              : yup.string().notRequired(),
          otherwise: () => yup.string().notRequired(),
        }),
      }),
      isSameAddress: yup
        .number()
        .required(t("addressFields.validations.communicationAddress")),
      countryCode: yup.string().required(t("commonValidations.countryCode")),
      contactNumber: yup
        .string()
        .required(
          applicantNo === 1
            ? t("additionalInfo.validations.primaryContactNumber")
            : t("additionalInfo.validations.contactNumber")
        )
        .test(
          "is-valid-phone",
          t("commonValidations.contactNumberInvalid"),
          (value, context) => {
            const { contactNumber, countryCode } = context.parent;
            return isValidPhoneNumberWithRules(countryCode, contactNumber);
          }
        ),

      alternateContactNumber: yup
        .string()
        .nullable()
        .test(
          "is-valid-alternate",
          t("commonValidations.alternateContactNumberInvalid"),
          function (value) {
            if (!value) return true;
            const { alternateCountryCode } = this.parent;
            return isValidPhoneNumberWithRules(alternateCountryCode, value);
          }
        )
        .test(
          "not-same-as-contact",
          "Alternate Contact Number should be different from the contact number",
          function (value) {
            if (!value) return true; // Skip if value is empty

            const { contactNumber } = this.parent;
            return value !== contactNumber;
          }
        ),

      emailAddress: yup
        .string()
        .matches(
          /^[\w.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
          t("commonValidations.emailAddressInvalid")
        )
        .required(t("commonValidations.emailAddress")),
      motherTongue: yup
        .string()
        .required(t("additionalInfo.validations.motherTongue")),
      maritalStatus: yup.string().when([], {
        is: () => hasContinuedAsMinor === false,
        then: () =>
          yup.string().required(t("additionalInfo.validations.maritalStatus")),
        otherwise: () => yup.string().notRequired(),
      }),

      anniversaryDate: yup.string().when(["maritalStatus"], {
        is: (maritalStatus) =>
          hasContinuedAsMinor === false &&
          maritalStatus === MARITAL_STATUS.MARRIED,
        then: () =>
          yup
            .string()
            .required(t("additionalInfo.validations.anniversaryDate"))
            .test(
              "is-after-dob",
              t("additionalInfo.validations.anniversaryAfterDob"),
              function (value) {
                const { dob } = this.parent;
                if (!value || !dob) return true; // Skip if either missing
                const anniversary = new Date(value);
                const birthDate = new Date(dob);
                return anniversary >= birthDate;
              }
            ),
        otherwise: () => yup.string().notRequired(),
      }),
    }),
  });

    const MOTHER_TONGUE_OPTIONS_DYNAMIC = useMemo(() => {
    const currentValue = additionalInfoFormik?.values?.motherTongue;
    const exists = MOTHER_TONGUE_OPTIONS?.some(opt => opt?.value === currentValue);
    if (currentValue && !exists) {
      return [...MOTHER_TONGUE_OPTIONS, { name: currentValue, value: currentValue }];
    }
    return MOTHER_TONGUE_OPTIONS;
  }, [additionalInfoFormik?.values?.motherTongue]);

  useEffect(() => {
    setApplicantObject(`applicant${applicantNo}`);
    if (applicantData[`applicant${applicantNo}`] !== null) {
      const contactDetails =
        applicantData[`applicant${applicantNo}`]?.contactDetails ||
        additionalInfoFormik.initialValues;
      // for setting maritalStatus according to careOf/relation
      const relation =
        applicantData[`applicant${applicantNo}`]?.personalDetails?.relation ||
        "";
        
      let maritalStatus = "";
      if (relation === IDENTITY.WIFE_OF || relation === IDENTITY.HUSBAND_OF) {
        maritalStatus = MARITAL_STATUS.MARRIED;
      } else if (
        relation === IDENTITY.SON_OF ||
        relation === IDENTITY.DAUGHTER_OF
      ) {
        maritalStatus = contactDetails?.maritalStatus || "";
      }
      if(!maritalStatus && applicantData?.relationBtApplicants === COMPARE_VALUE.Spouse && applicantNo===2){
        maritalStatus =MARITAL_STATUS.MARRIED
      }
      additionalInfoFormik.setValues({
        ...contactDetails,
        alternateCountryCode:
          applicantData[`applicant${applicantNo}`]?.contactDetails
            ?.alternateContactNumber || "IN",
        dob:
          applicantData[`applicant${applicantNo}`]?.personalDetails?.dob || "",
        maritalStatus,
      });
    }
    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicantData, applicantNo]);

  useEffect(() => {
    additionalInfoFormik.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [additionalInfoFormik?.values]);

  useEffect(() => {
    if (
      applicantData[`applicant${applicantNo}`]?.contactDetails
        ?.alternateContactNumber
    ) {
      setShowAlternateNumberField(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasContinuedAsMinor) {
      additionalInfoFormik?.setFieldValue(
        "maritalStatus",
        MARITAL_STATUS.SINGLE
      );
      additionalInfoFormik?.setFieldValue("anniversaryDate", null);
    }
  }, []);

  const handleSnackbarClose = () => {
    setSnackBarconfig({
      open: false,
      message: "",
      severity: "",
    });
  };

  const handleOnChange = (e, index, key, list) => {
    let selectedValue = list[index]?.value;
    additionalInfoFormik.setFieldValue(key, selectedValue);

    if (key === "isSameAddress" && selectedValue === 0) {
      additionalInfoFormik.setFieldValue("communicationAddress", {
        areaName: "",
        houseNumber: "",
        city: "",
        state: "",
        country: "",
        pinCode: "",
      });
    }
  };

  const handlePlaceChanged = (data) => {
    additionalInfoFormik.setFieldValue(
      "communicationAddress.city",
      data?.city || ""
    );
    additionalInfoFormik.setFieldValue(
      "communicationAddress.pinCode",
      data?.pinCode || ""
    );
    additionalInfoFormik.setFieldValue(
      "communicationAddress.state",
      data?.state || ""
    );
    additionalInfoFormik.setFieldValue(
      "communicationAddress.country",
      data?.country || ""
    );
  };

  const handlePlaceChangedPermanent = (data) => {
    additionalInfoFormik.setFieldValue(
      "permanentAddress.city",
      data?.city || ""
    );
    additionalInfoFormik.setFieldValue(
      "permanentAddress.pinCode",
      data?.pinCode || ""
    );
    additionalInfoFormik.setFieldValue(
      "permanentAddress.state",
      data?.state || ""
    );
    additionalInfoFormik.setFieldValue(
      "permanentAddress.country",
      data?.country || ""
    );
  };

  const shouldShowMaritalStatus = () => {
    const isSpouse =
      applicantData?.relationBtApplicants === COMPARE_VALUE.Spouse;
    const isFillingAs = applicantNo === applicantData?.fillingAs;
    const isSecondApplicant =
      applicantData?.noOfApplicants === 2 && applicantNo === 2;

    return (
      !hasContinuedAsMinor &&
      !(isSpouse && (applicantNo === 1 || isFillingAs || isSecondApplicant))
    );
  };

  const handlePrevious = () => {
    dispatch(setApplicantActiveStep(applicantActiveStep - 1));
  };

  // Helper function to create the personal details payload
  const createPersonalDetailsPayload = (saveForLaterFlag = false) => {
    // Check if the current applicant is a minor
    const isGuardianNeeded =
      applicantData?.applicant1?.hasContinuedAsMinor || false;

    const payload = {
      applicantNumber: parseInt(applicantNo),
      saveForLater: saveForLaterFlag,
      opportunityId: oppId,
      isGuardianNeeded: isGuardianNeeded,
      hasContinuedAsMinor,
      lastStep: getLastStep(applicantData?.lastStep, activeStep),
      contactDetails: {
        permanentAddress: {
          areaName: additionalInfoFormik?.values?.permanentAddress?.areaName,
          houseNumber:
            additionalInfoFormik?.values?.permanentAddress?.houseNumber,
          city: additionalInfoFormik?.values?.permanentAddress?.city,
          state: additionalInfoFormik?.values?.permanentAddress?.state,
          country: additionalInfoFormik?.values?.permanentAddress?.country,
          pinCode: additionalInfoFormik?.values?.permanentAddress?.pinCode,
        },
        communicationAddress: {
          areaName:
            additionalInfoFormik?.values?.isSameAddress === 1
              ? additionalInfoFormik?.values?.permanentAddress?.areaName
              : additionalInfoFormik?.values?.communicationAddress?.areaName,
          houseNumber:
            additionalInfoFormik?.values?.isSameAddress === 1
              ? additionalInfoFormik?.values?.permanentAddress?.houseNumber
              : additionalInfoFormik?.values?.communicationAddress?.houseNumber,
          city:
            additionalInfoFormik?.values?.isSameAddress === 1
              ? additionalInfoFormik?.values?.permanentAddress?.city
              : additionalInfoFormik?.values?.communicationAddress?.city,
          state:
            additionalInfoFormik?.values?.isSameAddress === 1
              ? additionalInfoFormik?.values?.permanentAddress?.state
              : additionalInfoFormik?.values?.communicationAddress?.state,
          country:
            additionalInfoFormik?.values?.isSameAddress === 1
              ? additionalInfoFormik?.values?.permanentAddress?.country
              : additionalInfoFormik?.values?.communicationAddress?.country,
          pinCode:
            additionalInfoFormik?.values?.isSameAddress === 1
              ? additionalInfoFormik?.values?.permanentAddress?.pinCode
              : additionalInfoFormik?.values?.communicationAddress?.pinCode,
        },
        isSameAddress: additionalInfoFormik?.values?.isSameAddress,
        countryCode: additionalInfoFormik?.values?.countryCode,
        contactNumber: additionalInfoFormik?.values?.contactNumber,
        emailAddress: additionalInfoFormik?.values?.emailAddress,
        motherTongue: additionalInfoFormik?.values?.motherTongue === OTHERS
          ? additionalInfoFormik?.values?.motherTongueIfOthers
          : additionalInfoFormik?.values?.motherTongue,
        maritalStatus: additionalInfoFormik?.values?.maritalStatus,
        alternateCountryCode:
          additionalInfoFormik?.values?.alternateCountryCode,
        alternateContactNumber:
          additionalInfoFormik?.values?.alternateContactNumber,
        anniversaryDate:
          additionalInfoFormik?.values?.maritalStatus === MARITAL_STATUS.MARRIED
            ? additionalInfoFormik?.values?.anniversaryDate
            : null,
      },
    };
    return payload;
  };

  const markAllTouched = (errors) => {
    const touched = {};
    Object.keys(errors).forEach((key) => {
      if (typeof errors[key] === "object" && errors[key] !== null) {
        touched[key] = markAllTouched(errors[key]);
      } else {
        touched[key] = true;
      }
    });
    return touched;
  };
  const handleSaveForm = async (type, addAuthorizedSignatory = false) => {
    const errors = await additionalInfoFormik.validateForm();

    if (Object.keys(errors).length === 0) {
      const payload = {
        ...createPersonalDetailsPayload(type === "saveForLater"),
      };

      try {
        const res = await dispatch(
          updateApplicantDetailsForm({
            path: route.UPDATE_APPLICANT_DATA,
            payload: payload,
            opportunityId: oppId,
          })
        ).unwrap();

        if (res) {
          if (addAuthorizedSignatory) {
            dispatch(setApplicantTotal(applicantNo + 1));
            dispatch(setKycVerificationActiveStep(0));
            dispatch(setApplicantActiveStep(0));
          }
        }

        dispatch(setHasContinuedAsMinorModal(false));

        if (hasContinuedAsMinor && applicantNo !== 1) {
          dispatch(setActiveStep(activeStep + 1));
        } else if (hasContinuedAsMinor && applicantNo === 1) {
          dispatch(setActiveStep(0));
          dispatch(setApplicantActiveStep(0));
        } else {
          dispatch(setApplicantActiveStep(2));
        }
      } catch (error) {
        setSnackBarconfig({
          open: true,
          message: getErrorMessage(error),
          severity: "error",
        });
      }
    } else {
      additionalInfoFormik.setTouched(markAllTouched(errors));
    }
  };

  const saveAndPreview = async (type) => {
    if (Object.keys(additionalInfoFormik.errors).length === 0) {
      const payload = {
        ...createPersonalDetailsPayload(type === "saveForLater"),
      };
      await dispatch(
        updateApplicantDetailsForm({
          path: route.UPDATE_APPLICANT_DATA,
          payload: payload,
          opportunityId: oppId,
        })
      )
        .unwrap()
        .then((res) => {
          if (res?.status === 200) {
            dispatch(setEditPreviewApplicant(false));
            history.push(`/customer-preview/${oppId}`);
            let completedSteps = {
              ...stepsCompleted,
              [activeStep]: true,
            };
            dispatch(setStepsCompleted(completedSteps));
            dispatch(setApplicantActiveStep(0));
          }
        });
    }
  };

  const handleAlternateNumberField = () => {
    setShowAlternateNumberField(true);
  };

  const handleDeleteAlternateNumberField = () => {
    additionalInfoFormik.setFieldValue("alternateContactNumber", "");
    additionalInfoFormik.setFieldValue("alternateCountryCode", "IN");
    setShowAlternateNumberField(false);
  };

  return (
    <div>
      <form
        onSubmit={(e) => e.preventDefault()}
        onReset={additionalInfoFormik.handleReset}
        noValidate
        autoComplete="off"
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              sx={{
                color: "#1A407D",
                fontSize: "14px",
                fontWeight: "600",
                lineHeight: "22px",
                mt: 1,
              }}
            >
              {t("additionalInfo.labels.permanentAddress")}
            </Typography>
          </Grid>
          <AddressFields
            addressType="permanentAddress"
            formik={additionalInfoFormik}
            handlePlaceChanged={handlePlaceChangedPermanent}
          />

          <Grid item xs={12}>
            <Typography
              sx={{
                color: "#1C252E",
                fontSize: "14px",
                fontWeight: "500",
                lineHeight: "22px",
              }}
            >
              {t("additionalInfo.labels.communicationAddress")}{" "}
              <span className="asteriskColor"> *</span>
            </Typography>
            {applicantNo === 1 ? (
              <Typography
                sx={{
                  color: "#1C252E",
                  fontSize: "12px",
                  fontWeight: "400",
                  lineHeight: "22px",
                  mt: "0px",
                }}
              >
                {t("additionalInfo.labels.sameAsPermanentAddress")}{" "}
                {/* Is the communication address same as the permanent address? */}
              </Typography>
            ) : (
              <Typography
                sx={{
                  color: "#1C252E",
                  fontSize: "12px",
                  fontWeight: "400",
                  lineHeight: "22px",
                  mt: "0px",
                }}
              >
                {t("additionalInfo.labels.sameAsAadhaarAddress")}{" "}
                {/* Do you want to continue with your Aadhaar address? */}
              </Typography>
            )}

            <BoxCheckInput
              field={{
                name: "isSameAddress",
                label: "",
                required: true,
                readonly: false,
                options: STATUS,
                grid: true,
                xsCol: 6,
                smCol: 6,
                mdCol: 3,
                lgCol: 3,
                mb: 0,
                handleOnChange,
              }}
              formik={additionalInfoFormik}
            />
          </Grid>

          {additionalInfoFormik?.values?.isSameAddress === 0 && (
            <AddressFields
              addressType="communicationAddress"
              formik={additionalInfoFormik}
              handlePlaceChanged={handlePlaceChanged}
            />
          )}

          <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                gap: 1,
              }}
            >
              <RHFPhoneInput
                name="contactNumber"
                countryCodeName="countryCode"
                label={
                  applicantNo === 1
                    ? t("additionalInfo.labels.primaryContactNumber")
                    : t("additionalInfo.labels.contactNumber")
                }
                country={additionalInfoFormik?.values?.countryCode || "IN"}
                formik={additionalInfoFormik}
                placeholder={
                  applicantNo === 1
                    ? t(
                        "additionalInfo.uploadPlaceholders.primaryContactNumber"
                      )
                    : t("additionalInfo.uploadPlaceholders.contactNumber")
                }
                required
                showDialCode={true}
              />
              <Tooltip enterTouchDelay={0} title="Add Alternate Contact Number">
                <AddCircleOutlineIcon
                  sx={{
                    color: "#1A407D",
                    marginTop:
                      (additionalInfoFormik?.touched?.countryCode &&
                        additionalInfoFormik?.errors?.countryCode) ||
                      (additionalInfoFormik?.touched?.contactNumber &&
                        additionalInfoFormik?.errors?.contactNumber)
                        ? "0px"
                        : "28px",
                  }}
                  onClick={handleAlternateNumberField}
                />
              </Tooltip>
            </Box>
          </Grid>
          {showAlternateNumberField && (
            <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  gap: 1,
                }}
              >
                <RHFPhoneInput
                  name="alternateContactNumber"
                  countryCodeName="alternateCountryCode"
                  label={t("commonLabels.alternateContactNumber")}
                  country={
                    additionalInfoFormik?.values?.alternateCountryCode || "IN"
                  }
                  formik={additionalInfoFormik}
                  placeholder={t("commonPlaceholders.alternateContactNumber")}
                  showDialCode={true}
                />
                <RemoveCircleIcon
                  sx={{ color: "#ff0000", marginTop: "28px" }}
                  onClick={handleDeleteAlternateNumberField}
                />
              </Box>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
            <TextFieldInput
              field={{
                name: "emailAddress",
                label: t("commonLabels.emailAddress"),
                required: true,
                readonly: false,
                placeholder: t("commonPlaceholders.emailAddress"),
                maxLength: 50,
                value: additionalInfoFormik?.values?.emailAddress,
              }}
              formik={additionalInfoFormik}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={6} xl={6}>
            <Typography
              sx={{
                marginBottom: "6px",
                fontSize: "14px",
                fontWeight: 500,
              }}
            >
              {t("additionalInfo.labels.motherTongue")}{" "}
              <span className="asteriskColor">*</span>
            </Typography>
            <SearchableAutoComplete
              field={{
                name: "motherTongue",
                otherField: "motherTongueIfOthers",
                options: MOTHER_TONGUE_OPTIONS_DYNAMIC,
                placeholder: t(
                  "additionalInfo.uploadPlaceholders.motherTongue"
                ),
                othersValue: OTHERS,
                required: true,
                readonly: false,
              }}
              formik={additionalInfoFormik}
            />
          </Grid>
          {shouldShowMaritalStatus() && (
            <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
              <BoxCheckInput
                field={{
                  name: "maritalStatus",
                  label: t("additionalInfo.labels.maritalStatus"),
                  required: true,
                  readonly: IsMarried ? true : false,
                  options: masterData?.Marital_Status,
                  grid: true,
                  xsCol: 4,
                  smCol: 4,
                  mdCol: 4,
                  lgCol: 4,
                  mb: 0,
                  handleOnChange,
                }}
                formik={additionalInfoFormik}
              />
            </Grid>
          )}
          {additionalInfoFormik?.values?.maritalStatus ===
            MARITAL_STATUS.MARRIED &&
            !hasContinuedAsMinor && (
              <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                <CustomDatePicker
                  label={t("additionalInfo.labels.anniversaryDate")}
                  value={additionalInfoFormik?.values?.anniversaryDate}
                  onChange={additionalInfoFormik.handleChange}
                  name="anniversaryDate"
                  minDate={
                    additionalInfoFormik?.values?.dob
                      ? new Date(additionalInfoFormik?.values?.dob)
                      : new Date(DATE_START_YEAR, 0, 1)
                  }
                  maxDate={new Date()}
                  error={
                    additionalInfoFormik.touched.anniversaryDate &&
                    Boolean(additionalInfoFormik.errors.anniversaryDate)
                  }
                  helperText={
                    additionalInfoFormik.touched.anniversaryDate &&
                    additionalInfoFormik.errors.anniversaryDate
                  }
                  infoText={
                    !additionalInfoFormik?.values?.dob
                      ? t("additionalInfo.uploadPlaceholders.anniversaryDate")
                      : ""
                  }
                  required={true}
                  disabled={!additionalInfoFormik?.values?.dob}
                />
              </Grid>
            )}
          {hasContinuedAsMinor && applicantNo !== 1 && (
            <AddAuthorizedSignatoryButton
              applicantNo={applicantNo}
              applicantTotal={applicantTotal}
              handleAddAuthorizedSignatory={() => {
                handleSaveForm("", true);
              }}
              type="applicant"
            />
          )}
        </Grid>
        <CommonButton
          handlePrevious={handlePrevious}
          handleNext={handleSaveForm}
          innerSteps={true}
          saveAndPreview={saveAndPreview}
          applicantNo={applicantNo}
          saveDraftPath={route?.UPDATE_APPLICANT_DATA}
          saveDraftData={{
            ...createPersonalDetailsPayload(true),
          }}
        />
        <SnackbarAlert
          open={snackbarConfig.open}
          message={snackbarConfig.message}
          severity={snackbarConfig.severity}
          onClose={handleSnackbarClose}
        />
      </form>
    </div>
  );
});

export default AdditionalInfo;
